import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { BuildingStorefrontIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Warehouse, CreateWarehouseData } from '../../types/warehouse';
import Toggle from '../common/Toggle';
import FormInput from '../common/FormInput';
import Modal from '../common/Modal';

interface WarehouseFormProps {
  warehouse: Warehouse | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateWarehouseData) => void;
}

const WarehouseForm: React.FC<WarehouseFormProps> = ({ warehouse, isOpen, onClose, onSave }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<CreateWarehouseData>({
    name: '',
    code: '',
    location: '',
    description: '',
    isActive: true,
    isDefault: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Adatok betöltése szerkesztés esetén
  useEffect(() => {
    if (warehouse) {
      setFormData({
        name: warehouse.name,
        code: warehouse.code,
        location: warehouse.location || '',
        description: warehouse.description || '',
        isActive: warehouse.isActive,
        isDefault: warehouse.isDefault || false
      });
    } else {
      // Új raktár esetén üres form
      setFormData({
        name: '',
        code: '',
        location: '',
        description: '',
        isActive: true,
        isDefault: false
      });
    }
  }, [warehouse]);

  // Form mezők változásának kezelése
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Hiba törlése, ha a mező ki lett töltve
    if (errors[name] && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Toggle komponensek kezelése
  const handleToggleChange = (name: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Kód mező automatikus kitöltése név alapján
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      name: value,
      // Csak akkor frissítjük a kódot automatikusan, ha még nem változtatta meg a felhasználó
      code: prev.code === '' || prev.code === prev.name.toLowerCase().replace(/\s+/g, '_')
        ? value.toLowerCase().replace(/\s+/g, '_')
        : prev.code
    }));
    
    // Hiba törlése, ha a mező ki lett töltve
    if (errors.name && value.trim()) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
  };

  // Form elküldése
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validáció
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }
    
    if (!formData.code.trim()) {
      newErrors.code = t('validation.required');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Adatok elküldése
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={warehouse ? t('warehouse.edit') : t('warehouse.create')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Fejléc */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg mb-2">
          <div className="flex items-center">
            <div className="text-white p-2 mr-3 rounded-lg shadow-md bg-gradient-to-r from-blue-500 to-indigo-600">
              <BuildingStorefrontIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-gray-800 dark:text-gray-200 font-medium text-md">
                {warehouse ? t('warehouse.edit') : t('warehouse.create')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {warehouse ? 'Raktár adatainak módosítása.' : 'Új raktár létrehozása a rendszerben.'}
              </p>
            </div>
          </div>
        </div>

        <div className="crm-card p-4 space-y-4">
          <FormInput
            id="name"
            name="name"
            label={t('warehouse.fields.name')}
            value={formData.name}
            onChange={handleNameChange}
            error={errors.name}
            required
          />

          <FormInput
            id="code"
            name="code"
            label={t('warehouse.fields.code')}
            value={formData.code}
            onChange={handleChange}
            error={errors.code}
            required
          />

          <FormInput
            id="location"
            name="location"
            label={t('warehouse.fields.location')}
            value={formData.location}
            onChange={handleChange}
            error={errors.location}
          />

          <div className="space-y-1">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('warehouse.fields.description')}
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-md py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>

          <div className="flex flex-col space-y-3 pt-2">
            <Toggle
              id="isActive"
              label={t('warehouse.fields.isActive')}
              checked={formData.isActive ?? true}
              onChange={(checked: boolean) => handleToggleChange('isActive', checked)}
            />

            <Toggle
              id="isDefault"
              label={t('warehouse.fields.isDefault')}
              checked={formData.isDefault ?? false}
              onChange={(checked: boolean) => handleToggleChange('isDefault', checked)}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-offset-gray-800"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="relative bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2 text-sm rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 flex items-center"
          >
            <CheckIcon className="h-4 w-4 mr-1.5" />
            {t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WarehouseForm; 